import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';

const HeroSection = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoLanguage, setVideoLanguage] = useState(language); // Separate state for video language
  const [loadBackgroundVideo, setLoadBackgroundVideo] = useState(false);
  const videoRef = useRef(null);
  const backgroundVideoRef = useRef(null);

  // Paint a tiny local poster immediately, then start the 3.5 MB decorative
  // background video only after the page's critical resources have loaded.
  useEffect(() => {
    let idleId;
    let timerId;

    const scheduleVideo = () => {
      if (navigator.connection?.saveData || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        return;
      }

      const enableVideo = () => setLoadBackgroundVideo(true);
      if ('requestIdleCallback' in window) {
        idleId = window.requestIdleCallback(enableVideo, { timeout: 1800 });
      } else {
        timerId = window.setTimeout(enableVideo, 350);
      }
    };

    if (document.readyState === 'complete') {
      scheduleVideo();
    } else {
      window.addEventListener('load', scheduleVideo, { once: true });
    }

    return () => {
      window.removeEventListener('load', scheduleVideo);
      if (idleId && 'cancelIdleCallback' in window) window.cancelIdleCallback(idleId);
      if (timerId) window.clearTimeout(timerId);
    };
  }, []);

  // Update video language when modal opens (use current site language as default)
  useEffect(() => {
    if (showVideoModal) {
      setVideoLanguage(language);
      document.body.dataset.reelsOpen = 'true';
      backgroundVideoRef.current?.pause();
    } else {
      delete document.body.dataset.reelsOpen;
      if (loadBackgroundVideo) backgroundVideoRef.current?.play().catch(() => {});
    }
  }, [showVideoModal, language, loadBackgroundVideo]);





  const handlePlayClick = () => {
    setShowVideoModal(true);
  };

  const handleVideoLanguageToggle = () => {
    // Toggle only the video language, not the site language
    setVideoLanguage(prev => prev === 'en' ? 'kn' : 'en');
  };

  const videoSrc = videoLanguage === 'kn' ? '/reels/kannada.mp4' : '/reels/english.mp4';

  return (
    <div className="relative h-[100vh] md:h-[95vh] overflow-visible">
      {/* Video background container with rounded bottom - extends to 75% height */}
      <div
        className="absolute top-0 left-0 right-0 overflow-hidden shadow-2xl"
        style={{
          height: '75%',
          borderBottomLeftRadius: '2rem',
          borderBottomRightRadius: '2rem'
        }}
      >
        <video
          ref={backgroundVideoRef}
          src={loadBackgroundVideo ? 'https://cdn.pixabay.com/video/2019/09/24/27197-362173819_large.mp4' : undefined}
          poster="/hero-poster.webp"
          autoPlay={loadBackgroundVideo}
          loop
          muted
          playsInline
          preload={loadBackgroundVideo ? 'metadata' : 'none'}
          className="w-full h-full object-cover"
        ></video>
        {/* Gradient Overlay - fades from opaque to transparent for seamless blend */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.3) 50%, rgba(0, 0, 0, 0.15) 85%, rgba(0, 0, 0, 0) 100%)'
          }}
        ></div>
      </div>

      {/* Content - positioned within the first 70% */}
      <div className="relative z-10 flex flex-col items-center justify-center px-4 pt-20 md:pt-0" style={{ height: '60%' }}>
        {/* Main Title */}
        <h1 className="text-white text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-extrabold mb-3 text-center font-montserrat leading-tight max-w-4xl">
          {t('hero.title')}
        </h1>

        {/* Subtitle */}
        <p className="text-white text-sm sm:text-base md:text-xl lg:text-2xl mb-6 md:mb-8 text-center font-montserrat max-w-3xl">
          {t('hero.subtitle')}
        </p>

        {/* Play Button - Same style as reels section */}
        <button
          onClick={handlePlayClick}
          className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/70 flex items-center justify-center shadow-xl transform hover:scale-110 transition-all duration-200 animate-pulse-slow"
          aria-label="Play tutorial video"
        >
          {/* Play icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-10 h-10 md:w-12 md:h-12 text-brand-blue ml-1"
          >
            <path d="M8 6.82v10.36c0 .79.87 1.27 1.54.84l8.14-5.18c.62-.39.62-1.29 0-1.69L9.54 5.98C8.87 5.55 8 6.03 8 6.82z" />
          </svg>
        </button>
      </div>

      {/* Desktop Search Button - Positioned at intersection (75% mark) */}
      <div className="hidden md:flex absolute left-0 right-0 z-20 justify-center" style={{ top: '75%', transform: 'translateY(-50%)' }}>
        <button
          onClick={() => navigate('/search')}
          className="flex items-center gap-2 px-24 py-4 rounded-xl bg-white border-2 border-brand-green text-brand-green hover:bg-brand-green hover:text-white transition-all shadow-lg text-base font-semibold font-montserrat"
          aria-label="Explore Lands"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
          </svg>
          Explore Lands
        </button>
      </div>

      {/* Video Modal */}
      {showVideoModal && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black bg-opacity-75"
          onClick={() => setShowVideoModal(false)}
        >
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            {/* Top bar with close button only */}
            <div className="absolute -top-14 left-0 right-0 flex items-center justify-end mb-2">
              {/* Close Button */}
              <button
                onClick={() => setShowVideoModal(false)}
                className="text-white hover:text-gray-300 bg-white bg-opacity-10 backdrop-blur-md hover:bg-opacity-20 rounded-lg p-2 transition-all border border-white border-opacity-20"
                aria-label="Close video"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Video Player */}
            <video
              ref={videoRef}
              key={videoSrc}
              src={videoSrc}
              controls
              autoPlay
              className="w-full rounded-lg shadow-2xl"
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes pulse-brand {
          0%, 100% {
            box-shadow: 0 10px 40px rgba(18, 121, 60, 0.3), 0 0 0 0 rgba(18, 121, 60, 0.7);
          }
          50% {
            box-shadow: 0 10px 50px rgba(18, 121, 60, 0.5), 0 0 0 8px rgba(18, 121, 60, 0);
          }
        }
        
        @keyframes ping-slow {
          0% {
            transform: scale(1);
            opacity: 0.75;
          }
          75%, 100% {
            transform: scale(1.3);
            opacity: 0;
          }
        }

        @keyframes pulse-slow {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }

        .animate-pulse-slow {
          animation: pulse-slow 2s ease-in-out infinite;
        }
      `}</style>

    </div>
  );
};

export default HeroSection;
